import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { getCurrentAccount } from '../../lib/auth';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, FileText, Wallet, TrendingUp, ExternalLink, AlertTriangle } from 'lucide-react';
import SubscriptionActions from '../../components/billing/SubscriptionActions';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return '—'; }
};

const Meter = ({ label, icon: Icon, value, max, suffix, testId }) => {
  const pct = max ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="bg-white border border-slate-200 p-5" data-testid={testId}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-3">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="text-2xl font-black tracking-tighter text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
        {value}{max != null ? <span className="text-base font-semibold text-slate-400"> / {max}</span> : null}
        {suffix ? <span className="text-sm font-semibold text-slate-400"> {suffix}</span> : null}
      </div>
      {max != null && (
        <div className="mt-3 h-1.5 bg-slate-100">
          <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
};

export default function BillingHome() {
  const navigate = useNavigate();
  const acct = getCurrentAccount();
  const [sub, setSub] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    try {
      const [s, inv] = await Promise.all([
        api.get('/subscription/current', { params: { account_id: acct?.id } }),
        api.get('/subscription/invoices', { params: { account_id: acct?.id } }),
      ]);
      setSub(s.data);
      setInvoices(inv.data?.invoices || []);
    } catch (e) {
      toast.error('Could not load billing');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, [acct?.id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading billing…
      </div>
    );
  }

  const plan = sub?.plan || {};
  const subscription = sub?.subscription || {};
  const bill = sub?.usage?.bill_credits || {};
  const ai = sub?.usage?.ai_credits || {};
  return (
    <div className="min-h-screen bg-slate-50" data-testid="billing-home">
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/billportal-logo.png" alt="BillPortal" className="h-8 w-8" />
            <span className="text-lg font-black tracking-tighter" style={{ fontFamily: 'Chivo, sans-serif' }}>Billing</span>
          </div>
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-semibold text-slate-500 hover:text-primary" data-testid="billing-back-btn">
            <ArrowLeft className="h-4 w-4" /> Dashboard
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-8">
        {/* Current plan */}
        <div className="bg-white border border-slate-200 p-6">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Current plan</div>
              <div className="text-3xl font-black tracking-tighter" style={{ fontFamily: 'Chivo, sans-serif' }} data-testid="current-plan-name">
                {plan.label || 'Free'}
              </div>
              <div className="text-sm text-slate-500 mt-1">
                {plan.usd_per_month ? `$${plan.usd_per_month.toLocaleString()}/mo` : 'Free forever'}
                {subscription?.current_period_end && plan.id !== 'free' && (
                  <span data-testid="current-plan-renewal"> · renews {fmtDate(subscription.current_period_end)}</span>
                )}
              </div>
            </div>
            <Button onClick={() => navigate('/billing/choose')} className="rounded-none font-bold uppercase tracking-wide text-xs" data-testid="change-plan-btn">
              {plan.id === 'free' ? 'Upgrade plan' : 'Change plan'}
            </Button>
          </div>
        </div>

        <SubscriptionActions sub={sub} accountId={acct?.id} onChanged={reload} />

        {/* Usage meters */}
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-3">This period's usage</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Meter label="Bills / month" icon={FileText} value={bill.consumed ?? 0} max={bill.allowance ?? 0} testId="usage-meter-bill-credits" />
            <Meter label="AI credits" icon={Wallet} value={Math.round(ai.balance ?? 0)} suffix={`≈ $${(ai.balance_usd ?? 0).toFixed(2)}`} testId="usage-meter-ai-credits" />
            <Meter label="Overage bills" icon={TrendingUp} value={bill.overage_bills ?? 0} suffix={`$${(bill.overage_usd_pending ?? 0).toFixed(2)} pending`} testId="usage-meter-overage" />
          </div>
        </div>

        {/* Invoices */}
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Invoices</div>
          <div className="bg-white border border-slate-200" data-testid="billing-invoices">
            {invoices.length === 0 ? (
              <div className="px-6 py-10 text-center text-sm text-slate-400" data-testid="billing-invoices-empty">
                No invoices yet. Paid-plan invoices appear here after your first payment.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                    <th className="text-left px-5 py-3 font-semibold">Invoice</th>
                    <th className="text-left px-5 py-3 font-semibold">Plan</th>
                    <th className="text-left px-5 py-3 font-semibold">Date</th>
                    <th className="text-right px-5 py-3 font-semibold">Amount</th>
                    <th className="text-right px-5 py-3 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.id} className="border-b border-slate-100 last:border-0" data-testid={`invoice-row-${inv.id}`}>
                      <td className="px-5 py-3 font-mono text-xs">
                        {inv.hosted_invoice_url ? (
                          <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">
                            {inv.number} <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : inv.number}
                      </td>
                      <td className="px-5 py-3">{inv.plan_label || inv.plan_id}</td>
                      <td className="px-5 py-3 text-slate-500">{fmtDate(inv.paid_at || inv.created_at)}</td>
                      <td className="px-5 py-3 text-right font-semibold">${(inv.amount_paid_usd ?? 0).toLocaleString()}</td>
                      <td className="px-5 py-3 text-right">
                        <span className="inline-block bg-primary/10 text-primary text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5">{inv.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
